import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { green, red } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";
import { DefaultTooltipContent } from "recharts";

import fpItemWipProfileService from "@plannel/services/fp/fp-item-wip-profile-service";
import numberUtil from "@plannel/utils/number-util";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — PlanResult/InoutStatus 파일럿에서 확인된 값(9)을
// 그대로 재사용한다(테넌트 단일값, fp-report-screen-guide.md §A-3c).
// TODO(MIG-T2): [itemwipprofile/ItemWipProfile] 조회조건 세팅 3종(버킷 목록/검색필드 표시/검색구간)의
//   테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰는 필드 3개(loadData/loadChartData의 getValues 기준,
// ItemWipProfileQueryRepository가 customerCdNm/itemCdNm/routeCdNm을 실제로 contains 조건으로 소비함 —
// InoutStatus와 동일하게 old에서도 실제로 동작하던 조회조건이다).
const SEARCH_FIELD_LABELS = { customerCdNm: "FP_CUSTOMER", routeCdNm: "FP_ROUTE", itemCdNm: "FP_ITEM" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// 백엔드가 그룹당 늘 이 3개 analysisIndicator 행만 내려준다(ItemWipProfileService#getItemWipProfiles) —
// old makeChartDataset의 direction(plus/minus)+useAbs 대응 — 생산은 양의 막대, 소비는 음의 막대로
// 반대 방향 스택(순증감 워터폴), 대기(재공)는 러닝밸런스라 값 그대로 선(line)으로 그린다
// (InoutStatus.js와 동일 패턴 — old ItemWipProfileChart.jsx/InoutStatusChart.jsx가 완전히 같은 파일).
const FP_PRODUCTION = "FP_PRODUCTION";
const FP_CONSUMPTION = "FP_CONSUMPTION";
const FP_WAIT = "FP_WAIT";
// 차트 범례 순서 대기-생산-소비(InoutStatus.js와 동일 지정) — 이 배열의 선언 순서는 실제 범례 순서를
// 결정하지 않는다. PivotChartComposed(공용)는 sortedLegend를 `sortBy(chartSeries, ["type"])`
// (PivotChartComposed.js:582)로 만들어 항상 "type" 문자열 알파벳 순으로 정렬하므로 bar류가
// line류보다 항상 먼저 온다 — 아래 FP_WAIT의 type: "0line" 참고.
const INDICATOR_ORDER = [FP_WAIT, FP_PRODUCTION, FP_CONSUMPTION];
// InoutStatus.js와 동일한 팔레트로 통일(다른 FP 리포트 화면들의 재고 트렌드 라인 색과 일관성 유지).
const INDICATOR_COLORS = {
  [FP_PRODUCTION]: "#6DE36F",
  [FP_CONSUMPTION]: "#FC5F67",
  [FP_WAIT]: "#4472C4",
};

// old ItemWipProfileChart.jsx는 getDefaultChartOptions(useAbsoluteValue=true)로 툴팁 값에 항상
// Math.abs()를 적용했다(소비를 음수로 뒤집어 그려도 부호 없이 보이는 이유) — PivotChartComposed
// (공용)의 기본 툴팁 포맷터는 음수면 "-"를 무조건 다시 붙여 화면별로 끌 수 없다. customTooltip으로
// recharts 기본 툴팁(DefaultTooltipContent)을 재사용하면서 formatter만 교체한다
// (fp-report-screen-guide.md §A-7, InoutStatus.js와 동일 패턴).
const AbsValueTooltip = (props) => (
  <DefaultTooltipContent {...props} formatter={(value) => numberUtil.formatNumber(Math.abs(value))} />
);

// 트리(행 그룹) 계층 — 품목상위그룹 > 품목 > 인벤토리 > 공정 > 고객 > (leaf)분석지표. old 컬럼정의의
// itemGroup 하위 전부(itemParentGrp/item/inventory/route/customer)가 `mergeRule`이라 Row Grouping을
// 선택했다(Tree Data가 아님, fp-report-screen-guide.md §A-6b) — InoutStatus.js와 완전히 동일한 계층.
const GROUP_FIELDS = [
  "itemParentGrpCd", "itemParentGrpNm", "itemCd", "itemNm", "inventoryCd", "inventoryNm", "routeCd", "routeNm",
  "customerCd", "customerNm"
];

const ITEM_GRP_CD_COL_ID = "itemParentGrpCdGrp";
const ITEM_GRP_NM_COL_ID = `ITEM_GRP_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_CD_COL_ID = "itemCdGrp";
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const INVENTORY_CD_COL_ID = "inventoryCdGrp";
const INVENTORY_NM_COL_ID = `INVENTORY_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ROUTE_CD_COL_ID = "routeCdGrp";
const ROUTE_NM_COL_ID = `ROUTE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const CUSTOMER_CD_COL_ID = "customerCdGrp";
const CUSTOMER_NM_COL_ID = `CUSTOMER_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const ITEM_GRP_NM_HIDE_KEY = "itemGrpNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";
const INVENTORY_NM_HIDE_KEY = "inventoryNmColsHide";
const ROUTE_NM_HIDE_KEY = "routeNmColsHide";
const CUSTOMER_NM_HIDE_KEY = "customerNmColsHide";

const ItemWipProfile = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // customerCdNm/routeCdNm/itemCdNm — Report 공통 정책(§A-9b)대로 조회 버튼이 없다. 자유 텍스트는
  // SearchOptsGroupRetrieve(TextFieldSearch 기반) 안의 Enter/검색아이콘으로만 커밋한다.
  const customerCdNmRef = useRef("");
  const routeCdNmRef = useRef("");
  const itemCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveItemWipProfile(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 재공대기(FP_WAIT) 행 강조 — 회색 행 배경 대신 분석 지표 컬럼부터 연두색(green[50])으로 통일한다
  // (InoutStatus.js와 동일 관례). 분석 지표 컬럼엔 배경색만 걸고, 값이 0보다 큰 피벗 셀엔 빨간 글자
  // 경고(shortageCellStyle)를 배경과 같이 적용한다.
  const analysisIndicatorHighlightStyle = (params) => (
    params.data?.analysisIndicator === FP_WAIT ? { backgroundColor: green[50] } : null
  );

  // old columnProperty(setCrossTabGridLayoutAndOptions)의 셀 강조 대응 — FP_WAIT 행은 항상
  // green[50] 배경이고, 값이 0보다 크면 추가로 'shortage-cell'(빨간 글자)로 표시한다
  // (analysisIndicatorHighlightStyle과 동일 기준, InoutStatus.js와 동일 패턴).
  const shortageCellStyle = (params) => {
    if (params.data?.analysisIndicator !== FP_WAIT) return null;

    return {
      backgroundColor: green[50],
      color: params.value > 0 ? red[500] : undefined
    };
  };

  // old columnProperty.valueCallback — 0 이하 값은 빈 셀로 보여준다(그리드 전용, 차트에는 적용하지 않음).
  const suppressNonPositive = (value) => (value === null || value === undefined || value <= 0 ? null : value);

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(itemParentGrpCd/Nm, itemCd/Nm, inventoryCd/Nm, routeCd/Nm,
  // customerCd/Nm, analysisIndicator)와 1:1. 5개 그룹은 행 그룹(트리), analysisIndicator는 leaf(실제
  // 데이터 행을 구분하는 값)라 field 컬럼이다.
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_GRP_NM_HIDE_KEY),
      cdColId: ITEM_GRP_CD_COL_ID, cdField: "itemParentGrpCd", cdHeader: "FP_ITEM_GRP_CD",
      nmColId: ITEM_GRP_NM_COL_ID, nmField: "itemParentGrpNm", nmHeader: "FP_ITEM_GRP_NM",
      hideKey: ITEM_GRP_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_NM_HIDE_KEY),
      cdColId: ITEM_CD_COL_ID, cdField: "itemCd", cdHeader: "FP_ITEM_CD",
      nmColId: ITEM_NM_COL_ID, nmField: "itemNm", nmHeader: "FP_ITEM_NM",
      hideKey: ITEM_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, INVENTORY_NM_HIDE_KEY),
      cdColId: INVENTORY_CD_COL_ID, cdField: "inventoryCd", cdHeader: "FP_INVENTORY_CD",
      nmColId: INVENTORY_NM_COL_ID, nmField: "inventoryNm", nmHeader: "FP_INVENTORY_NM",
      hideKey: INVENTORY_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ROUTE_NM_HIDE_KEY),
      cdColId: ROUTE_CD_COL_ID, cdField: "routeCd", cdHeader: "FP_ROUTE_CD",
      nmColId: ROUTE_NM_COL_ID, nmField: "routeNm", nmHeader: "FP_ROUTE_NM",
      hideKey: ROUTE_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, CUSTOMER_NM_HIDE_KEY),
      cdColId: CUSTOMER_CD_COL_ID, cdField: "customerCd", cdHeader: "FP_CUSTOMER_CD",
      nmColId: CUSTOMER_NM_COL_ID, nmField: "customerNm", nmHeader: "FP_CUSTOMER_NM",
      hideKey: CUSTOMER_NM_HIDE_KEY, width: 150
    }),

    // leaf(분석 지표) — old displayCallback가 항상 transLangKey(value)로 번역해서 보여준다.
    {
      headerName: "FP_ANALYSIS_INDICATOR", field: "analysisIndicator", colId: "analysisIndicator", width: 150,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"],
      valueFormatter: (params) => t(params.value),
      cellStyle: analysisIndicatorHighlightStyle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // 일버킷일 때 자식 헤더로 오는 요일 코드 다국어+토/일 강조색은 common/grid.js
  // buildDayBucketPivotColumnDefs가 처리한다(old setColumnColorOnSunSat 대응, §A-6d/§A-6f).
  // shortageCellStyle은 이 화면 고유 강조라 childExtraProps로 얹는다.
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t, {
    childExtraProps: () => ({ cellStyle: shortageCellStyle })
  });

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  // valueCallback(0 이하 → null)도 여기서 함께 적용한다(그리드 전용).
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm,
      itemCd: row.itemCd, itemNm: row.itemNm,
      inventoryCd: row.inventoryCd, inventoryNm: row.inventoryNm,
      routeCd: row.routeCd, routeNm: row.routeNm,
      customerCd: row.customerCd, customerNm: row.customerNm,
      analysisIndicator: row.analysisIndicator
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = suppressNonPositive(row.pivotData?.[index]);
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      customerCdNm: customerCdNmRef.current,
      routeCdNm: routeCdNmRef.current,
      itemCdNm: itemCdNmRef.current,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — PlanResult.js/InoutStatus.js와 동일한 슬롯 재활용 원칙. 5단
  // 계층을 originLoc/destLoc/customer/item/workCenter(자원 슬롯은 이 화면에 대응 개념이 없어 미사용)
  // 순서에 맞춰 싣는다.
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.itemParentGrpCd ?? null, originLocCdHeader: t("FP_ITEM_GRP_CD"),
      originLocNm: target.itemParentGrpNm ?? null, originLocNmHeader: t("FP_ITEM_GRP_NM"),
      destLocCd: target.itemCd ?? null, destLocCdHeader: t("FP_ITEM_CD"),
      destLocNm: target.itemNm ?? null, destLocNmHeader: t("FP_ITEM_NM"),
      customerCd: target.inventoryCd ?? null, customerCdHeader: t("FP_INVENTORY_CD"),
      customerNm: target.inventoryNm ?? null, customerNmHeader: t("FP_INVENTORY_NM"),
      itemCd: target.routeCd ?? null, itemCdHeader: t("FP_ROUTE_CD"),
      itemNm: target.routeNm ?? null, itemNmHeader: t("FP_ROUTE_NM"),
      workCenterCd: target.customerCd ?? null, workCenterCdHeader: t("FP_CUSTOMER_CD"),
      workCenterNm: target.customerNm ?? null, workCenterNmHeader: t("FP_CUSTOMER_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. old updateChartFromGrid와 동일 — 상위 레벨을 클릭하면
  // 하위 코드는 비워서 보낸다(백엔드가 useGrouping:true일 때 이 레벨로 집계).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {
      itemParentGrpCd: target.itemParentGrpCd ?? "", itemCd: target.itemCd ?? "",
      inventoryCd: target.inventoryCd ?? "", routeCd: target.routeCd ?? "", customerCd: target.customerCd ?? ""
    };
    const prev = prevChartParamsRef.current;
    if (prev && Object.keys(nextParams).every(field => prev[field] === nextParams[field])) return;
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    // old loadChartData는 useGrouping:true(그리드 조회의 false와 반대) — 드릴다운 레벨로 백엔드가
    // 집계해서 내려준다.
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    // TODO(MIG-T2): [itemwipprofile/ItemWipProfile] 그리드 컬럼 필터(값 목록 체크박스) +
    //   analysisIndicator 필터 미이관.
    //   old: itemParentGrpCd/itemCd/inventoryCd/routeCd/customerCd/analysisIndicator에 RealGrid
    //        autoFilter, 필터 선택값을 차트 조회의 changes(FilterParameter)로 함께 전송
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다. 백엔드는 changes를 계속 받으므로 2차에 트리거 UI만
    //        얹으면 된다(InoutStatus.js와 동일한 결정) — 지금은 빈 필터로 보낸다.
    fpItemWipProfileService.getItemWipProfileChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = data ?? [];

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartLabels([]);
          return;
        }

        setChartLabels(chartHeader);
        // old makeChartDataset — 생산은 양의 막대, 소비는 음의 막대(direction:minus, useAbs:true),
        // 대기는 값 그대로 선(line, useAbs:false). 백엔드가 늘 이 3개 analysisIndicator 행만
        // 내려주므로(그룹 depth와 무관) 런타임 groupKey 없이 고정 배열로 매핑한다(InoutStatus.js와 동일).
        setChartData(rows.map(r => {
          const point = { analysisIndicator: r.analysisIndicator };
          const sign = r.analysisIndicator === FP_CONSUMPTION ? -1 : 1;
          chartHeader.forEach((header, index) => {
            const value = r.pivotData?.[index] ?? 0;
            point[header] = r.analysisIndicator === FP_WAIT ? value : sign * Math.abs(value);
          });
          return point;
        }));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveItemWipProfile = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd, useGrouping: false };

    gridRef.current?.api?.setGridOption("loading", true);

    fpItemWipProfileService.getItemWipProfiles(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 품목상위그룹 기준으로 차트를 그리고, 그리드의 선택 셀도 그 행으로 옮긴다.
        pendingFocusRef.current = { rowIndex: 0, colId: ITEM_GRP_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ itemParentGrpCd: rows[0].itemParentGrpCd, itemParentGrpNm: rows[0].itemParentGrpNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const ITEM_GRP_LEVEL_COL_IDS = new Set([ITEM_GRP_CD_COL_ID, ITEM_GRP_NM_COL_ID]);
  const ITEM_LEVEL_COL_IDS = new Set([ITEM_CD_COL_ID, ITEM_NM_COL_ID]);
  const INVENTORY_LEVEL_COL_IDS = new Set([INVENTORY_CD_COL_ID, INVENTORY_NM_COL_ID]);
  const ROUTE_LEVEL_COL_IDS = new Set([ROUTE_CD_COL_ID, ROUTE_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith(...)` 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것). groupHideOpenParents
  // 때문에 그룹 행(접힌 대표 행)과 leaf(analysisIndicator) 행 둘 다 다뤄야 한다.
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (ITEM_GRP_LEVEL_COL_IDS.has(colId)) {
      return { itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm };
    }
    if (ITEM_LEVEL_COL_IDS.has(colId)) {
      return {
        itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm,
        itemCd: row.itemCd, itemNm: row.itemNm
      };
    }
    if (INVENTORY_LEVEL_COL_IDS.has(colId)) {
      return {
        itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm,
        itemCd: row.itemCd, itemNm: row.itemNm, inventoryCd: row.inventoryCd, inventoryNm: row.inventoryNm
      };
    }
    if (ROUTE_LEVEL_COL_IDS.has(colId)) {
      return {
        itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm,
        itemCd: row.itemCd, itemNm: row.itemNm, inventoryCd: row.inventoryCd, inventoryNm: row.inventoryNm,
        routeCd: row.routeCd, routeNm: row.routeNm
      };
    }
    return row;
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — PlanResult.js/
  // InoutStatus.js와 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/버킷)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b). 자유 텍스트
  // (customerCdNm/routeCdNm/itemCdNm)는 SearchOptsGroupRetrieve 내부 수동 커밋으로만 조회된다.
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  const pivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: INDICATOR_ORDER.map(indicator => (
      indicator === FP_WAIT
        ? {
          // fill을 같이 넘기면 PivotChartComposed(공용)가 line 렌더링 시 stroke를 fill 값으로
          // 덮어써 선이 투명해지는 부작용이 있다(점만 보이고 선이 안 보이는 원인) — fill 없이
          // stroke만 넘긴다. type을 "0line"으로 쓰는 이유(위 INDICATOR_ORDER 주석 참고) —
          // PivotChartComposed의 렌더 분기는 "bar"/"area"가 아니면 전부 <Line>으로 그리므로 시각적
          // 렌더링엔 영향이 없고, "0" 접두어로 알파벳 정렬에서 "bar"보다 앞에 오게 만들어 범례를
          // 대기(line)가 맨 앞에 오도록 강제한다 — 공용 파일을 안 건드리고 범례 순서만 바꾸는 유일한 방법.
          field: indicator, text: indicator, type: "0line", groupName: t("QTY"),
          typeOptions: { stroke: INDICATOR_COLORS[indicator], name: t(indicator) }
        }
        : {
          field: indicator, text: indicator, type: "bar", groupName: t("QTY"),
          typeOptions: { stackId: "itemWipProfile", fill: INDICATOR_COLORS[indicator], name: t(indicator) }
        }
    )),
    include: chartLabels,
    // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
    // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartLabels, t]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // Report 화면 bucketCase 배정표(fp-report-screen-guide.md §A-3) — ResProdPlan/
          // ResrcUtilization/JobChangeTimeInfo 3개 화면만 WITHOUT_HOUR, 이 화면은 그 예외에 없으므로
          // WITH_HOUR(InoutStatus.js와 동일).
          bucketCase={BUCKET_CASE.WITH_HOUR}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{ customerCdNm: customerCdNmRef, routeCdNm: routeCdNmRef, itemCdNm: itemCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveItemWipProfile}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-item-wip-profile-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* TODO(MIG-T2): [itemwipprofile/ItemWipProfile] 차트 장식성 인터랙션 3종 미이관.
                old: ChartHatchPatternPlugin(빗금), highlightChartByLabel/resetChartHighlighting(hover dim),
                     ChartDataLabelPlugin(막대 위 수치)
                사유: recharts/PivotChartComposed에 대응 훅이 없고, 1차 목표(조회·표시)에 불필요 — 가이드 §A-7에서 Tier2 확정 */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
              customTooltip={AbsValueTooltip}
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ItemWipProfile);
